// PayrollPeriod
import { useState } from "react";
import { Reactable } from "../../components/ui/reactable";
import { Button } from "../../components/ui/button";
import { Edit, Trash, Calendar } from "lucide-react";
import { PAYROLL_PERIOD } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../components/ui/fieldStyles";
import { ConfirmPopupModal, DialogModal } from "../../components/ui/dialogModal";
import { Input, Textarea } from "../../components/ui/input";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { checkUserPermissionFunc, truncateText } from "../../components/common/CommonFunctions";
import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Radio, Select } from "antd";
import { monthEnum } from "../../constants/enum";

const MIN_PAYROLL_PERIOD_DAYS = 10;

const payrollPeriodTemplateSpan = (startDay, endDay) => {
    const start = parseInt(startDay, 10) || 0;
    const end = parseInt(endDay, 10) || 0;
    if (start <= 0 || end <= 0) return 0;
    if (end >= start) return end - start + 1;
    return (28 - start + 1) + end;
};

const PayrollPeriod = () => {
    const { create, update } = useApiMethods();
    const [PayrollPeriodsData, setPayrollPeriodsData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [serverTrigger, setServerTrigger] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editId, setEditId] = useState(null);

    const { add: addPermission, edit: editPermission, draft: draftPermission } = checkUserPermissionFunc('configuration', 'payroll period', 'SubMenu')

    // Form state for payroll period - matching old code structure
    const [formData, setFormData] = useState({
        code: "",
        period: "customize", // "month_wise" or "customize"
        payroll_period_name: "",
        custom_month: [],
        custom_start_day: 0,
        custom_end_day: 0,
        month: [0],
        start_day: 1,
        end_day: 31,
        notes: "",
        is_active: true,
        is_draft: false
    });

    // Reset form function
    const resetForm = () => {
        setFormData({
            code: "",
            period: "customize",
            payroll_period_name: "",
            custom_month: [],
            custom_start_day: 0,
            custom_end_day: 0,
            month: [0],
            start_day: 1,
            end_day: 31,
            notes: "",
            is_active: true,
            is_draft: false
        });
        setEditMode(false);
        setEditId(null);
        setOpenDialog(false);
        setSelectedRow(null);
    };

    // Handle form field changes - matching old code logic
    const handleInputChange = (field, value) => {
        const updatedFormData = { ...formData, [field]: value };

        // Handle special cases like in old code
        switch (field) {
            case "custom_start_day":
                updatedFormData[field] = parseInt(value || 0);
                if (!value) {
                    updatedFormData.custom_end_day = 0;
                }
                let endDay = parseInt(updatedFormData.custom_start_day || 0) - 1;
                updatedFormData["custom_end_day"] = endDay === -1 ? 0 : (endDay === 0 ? 31 : endDay);
                break;
            case "custom_month":
            case "month":
                updatedFormData[field] = value?.includes(0) ? [0] : value;
                break;
        }

        setFormData(updatedFormData);
    };

    // Handle radio change - matching old code logic
    const handleRadioChange = (e) => {
        const { value } = e.target;
        let updatedFormData = { ...formData };

        if (value === "month_wise") {
            updatedFormData = {
                ...updatedFormData,
                period: "month_wise",
                start_day: 1,
            };
        } else if (value === "customize") {
            updatedFormData = {
                ...updatedFormData,
                period: "customize",
            };
        }
        setFormData(updatedFormData);
    };

    // Validation functions from old code
    const validateRangeMandatory = (min, max) => ({
        validator(_, value) {
            if (!value && value !== 0) {
                return Promise.reject(new Error('This field is required'));
            }
            const numValue = parseInt(value);
            if (isNaN(numValue) || numValue < min || numValue > max) {
                return Promise.reject(new Error(`Must be between ${min} and ${max}`));
            }
            return Promise.resolve();
        },
    });

    const validateStartEndDay = (_, value) => {
        const startDay = formData.period === "month_wise" ? formData.start_day : formData.custom_start_day;
        if (value && startDay && parseInt(value) <= parseInt(startDay)) {
            return Promise.reject(new Error('End day must be greater than start day'));
        }
        return Promise.resolve();
    };

    // Handle key down for day fields - from old code
    const handleKeyDown = (e) => {
        // Allow only numbers, backspace, and delete keys
        if (!/^\d$/.test(e.key) && e.key !== "Backspace" && e.key !== "Delete" &&
            e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Home" && e.key !== "End") {
            e.preventDefault();
        }
        const newValue = e.target.value === "" ? e.key : e.target.value + e.key;
        if ((e.key !== "Backspace" && e.key !== "Delete") && (parseInt(newValue, 10) < 1 || parseInt(newValue, 10) > 31)) {
            e.preventDefault();
        }
    };

    // Table columns for payroll period data
    const PayrollPeriodColumns = [
        {
            accessor: "code",
            Header: "Payroll Code",
            sortable: true,
            filterable: true,
            width: "120px",
        },
        {
            accessor: "payroll_period_name",
            Header: "Period Name",
            sortable: true,
            filterable: true,
            width: "150px",
        },
        {
            accessor: "start_day",
            Header: "Start Day",
            sortable: true,
            width: "100px",
            Cell: ({ value }) => value || "-"
        },
        {
            accessor: "end_day",
            Header: "End Day",
            sortable: true,
            width: "100px",
            Cell: ({ value }) => value || "-"
        },
        {
            accessor: "period",
            Header: "Period Type",
            sortable: true,
            filterable: true,
            filterType: "select",
            filterOptions: [
                { value: "month_wise", label: "Month Wise" },
                { value: "customize", label: "Customize" },
            ],
            width: "120px",
            Cell: ({ value }) => {
                const periodTypes = {
                    "month_wise": "Month Wise",
                    "customize": "Customize"
                };
                return periodTypes[value] || "-";
            }
        },
        {
            accessor: "notes",
            Header: "Notes",
            sortable: false,
            filterable: true,
            width: "150px",
            Cell: ({ value }) => truncateText(value, 20, "250px", "balance", "No Description")
        },
        {
            accessor: "is_active",
            Header: "Status",
            sortable: false,
            filterable: true,
            width: "100px",
            Cell: ({ value }) => (
                <span
                    className={cn(
                        "px-2 py-1 text-xs font-medium rounded-full",
                        value
                            ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                            : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    )}
                >
                    {value ? "Active" : "Inactive"}
                </span>
            ),
        },
        ...(editPermission ? [
            {
                accessor: "action",
                Header: "Actions",
                sortable: false,
                width: "100px",
                Cell: ({ row }) => {
                    const handleOpenDialog = () => {
                        const rowData = row;
                        setEditMode(true);
                        setEditId(rowData?.code);
                        setFormData({
                            id: rowData?.id || null,
                            code: rowData?.code || "",
                            period: rowData?.period || "customize",
                            payroll_period_name: rowData?.payroll_period_name || "",
                            custom_month: rowData?.period === "customize" ? rowData?.month || [] : [],
                            custom_start_day: rowData?.period === "customize" ? rowData?.start_day || 0 : 0,
                            custom_end_day: rowData?.period === "customize" ? rowData?.end_day || 0 : 0,
                            month: rowData?.period === "month_wise" ? rowData?.month || [0] : [0],
                            start_day: rowData?.period === "month_wise" ? rowData?.start_day || 1 : 1,
                            end_day: rowData?.period === "month_wise" ? rowData?.end_day || 31 : 31,
                            notes: rowData?.notes || "",
                            is_active: rowData?.is_active ?? true,
                            is_draft: rowData?.is_draft || false
                        });
                        setOpenDialog(true);
                    };

                    const handleDeleteClick = (row) => {
                        setSelectedRow(row);
                        setIsDeleteConfirmOpen(true);
                    };

                    return (
                        <div className="flex items-center gap-2">
                            {/* Edit Button */}
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                                onClick={handleOpenDialog}
                            >
                                <Edit className="h-4 w-4" />
                            </Button>

                            {/* Delete Button (only if row is draft) */}
                            {row?.is_draft && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/30"
                                    onClick={() => handleDeleteClick(row)}
                                >
                                    <Trash className="h-4 w-4" />
                                </Button>
                            )}
                        </div>
                    );
                },
            },
        ] : [])
    ];

    // Prepare payload for API - matching old code logic
    const preparePayload = (isDraft = false) => {
        const payload = {
            ...formData,
            month: formData.period === "month_wise" ? formData.month : formData.custom_month,
            start_day: formData.period === "month_wise"
                ? formData.start_day ? parseInt(formData.start_day) : 1
                : formData.custom_start_day ? parseInt(formData.custom_start_day) : 0,
            end_day: formData.period === "month_wise"
                ? formData.end_day ? parseInt(formData.end_day) : 31
                : formData.custom_end_day ? parseInt(formData.custom_end_day) : 0,
            is_draft: isDraft
        };

        // Remove temporary fields like in old code
        delete payload.custom_month;
        delete payload.custom_start_day;
        delete payload.custom_end_day;

        return payload;
    };

    // Save as draft function
    const handleDraft = async () => {
        try {
            setLoading(true);
            const payload = preparePayload(true);

            const response = await create(PAYROLL_PERIOD, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "Payroll period saved to draft successfully");
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || "Failed to save draft");
            }
        } catch (error) {
            console.error("Error saving draft:", error);
            ToastMsg("error", "Failed to save draft");
        } finally {
            setLoading(false);
        }
    };

    // Delete draft function
    const handleDraftDelete = async () => {
        try {
            setLoading(true);
            if (!selectedRow) return;

            const payload = {
                id: selectedRow.id,
                is_active: false,
                draft_delete: true,
                is_draft: true
            };

            const response = await update(PAYROLL_PERIOD, payload);

            if (response?.status === "success") {
                ToastMsg("success", response.message || "Draft payroll period deleted successfully");
                setServerTrigger((prev) => !prev);
                setIsDeleteConfirmOpen(false);
                setSelectedRow(null);
            } else {
                ToastMsg("error", response?.message || "Failed to delete draft payroll period");
            }
        } catch (error) {
            console.error("Error deleting draft:", error);
            ToastMsg("error", "Failed to delete draft payroll period");
        } finally {
            setLoading(false);
        }
    };

    // Save or update function
    const handleSave = async () => {
        // Validation
        if (!formData.payroll_period_name?.trim()) {
            ToastMsg("error", "Payroll period name is required");
            return;
        }

        const startDay = formData.period === "month_wise" ? formData.start_day : formData.custom_start_day;
        const endDay = formData.period === "month_wise" ? formData.end_day : formData.custom_end_day;
        const span = payrollPeriodTemplateSpan(startDay, endDay);
        if (span < MIN_PAYROLL_PERIOD_DAYS) {
            ToastMsg(
                "error",
                `Payroll period must span at least ${MIN_PAYROLL_PERIOD_DAYS} days (configured span is ${span} days).`
            );
            return;
        }

        try {
            setLoading(true);
            const payload = preparePayload(false);

            let response;

            if (editMode && formData.id) {
                response = await update(PAYROLL_PERIOD, payload);
            } else {
                response = await create(PAYROLL_PERIOD, payload);
            }

            if (response?.status === "success") {
                ToastMsg("success", response.message || `Payroll period ${editMode ? 'updated' : 'saved'} successfully`);
                resetForm();
                setServerTrigger((prev) => !prev);
            } else {
                ToastMsg("error", response?.message || `Failed to ${editMode ? 'update' : 'save'} payroll period`);
            }
        } catch (error) {
            console.error("Error saving payroll period:", error);
            ToastMsg("error", `Failed to ${editMode ? 'update' : 'save'} payroll period`);
        } finally {
            setLoading(false);
        }
    };

    return (
        <ContainerCard>
            <ModuleTitleCard
                Icon={Calendar}
                title={'Payroll Period'}
                subtitle="Configure payroll calculation periods"
                totalRecords={totalRecords}
                buttonEnable={!!addPermission}
                setOpenDialog={setOpenDialog}
                buttonName={'Add Payroll Period'}
            />

            <Reactable
                columns={PayrollPeriodColumns}
                data={PayrollPeriodsData}
                setData={setPayrollPeriodsData}
                setTotalCount={setTotalRecords}
                url={PAYROLL_PERIOD}
                serverSide={true}
                serverTrigger={serverTrigger}
                rowSize={editPermission ? "sm" : null}
                isCSVRequired={true}
                isPDFRequired={true}
                isDraftRequired={!!draftPermission}
                isDateRequired={false}
            />

            {/* Add/Edit Modal with payroll period fields */}
            <DialogModal
                open={openDialog}
                onClose={resetForm}
                onDraft={handleDraft}
                onSave={handleSave}
                title={editMode ? "Edit Payroll Period" : "Add Payroll Period"}
                closeLabel="Cancel"
                saveLabel={editMode ? "Update" : "Save"}
                draftLabel="Draft"
                editMode={editMode}
                loading={loading}
                width="800px"
            >
                <form className="space-y-4">
                    {/* Period Type Radio */}
                    <div className="space-y-2">
                        <label className={modalFieldLabelClass}>
                            Period Type
                        </label>
                        <Radio.Group
                            value={formData.period}
                            onChange={handleRadioChange}
                            className="w-full"
                        >
                            <Radio value="month_wise" className=" text-sm font-medium text-gray-700 dark:text-gray-300">Month Wise</Radio>
                            <Radio value="customize" className=" text-sm font-medium text-gray-700 dark:text-gray-300">Customize</Radio>
                        </Radio.Group>
                    </div>

                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                            Payroll Period Details
                        </h4>

                        <div className="grid grid-cols-2 gap-6">
                            {/* Left Column */}
                            <div className="space-y-4">
                                {/* Payroll Period Code (only in edit mode) */}
                                {editMode && (
                                    <div className="space-y-2">
                                        <label className={modalFieldLabelClass}>
                                            Payroll Period Code
                                        </label>
                                        <Input
                                            value={formData.code}
                                            disabled
                                            placeholder="Auto-generated code"
                                            className={cn(
                                                "w-full rounded-md transition-colors duration-200",
                                                "border-gray-300 dark:border-gray-600",
                                                "bg-muted text-muted-foreground",
                                            )}
                                        />
                                    </div>
                                )}

                                {/* Payroll Period Name */}
                                <div className="space-y-2">
                                    <label className={modalFieldLabelClass}>
                                        Payroll Period Name <span className="text-red-500">*</span>
                                    </label>
                                    <Input
                                        value={formData.payroll_period_name}
                                        onChange={(e) => handleInputChange("payroll_period_name", e.target.value)}
                                        placeholder="Enter payroll period name"
                                        disabled={loading}
                                        className={cn(
                                            "w-full rounded-md transition-colors duration-200",
                                            "border-gray-300 dark:border-gray-600",
                                            "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                        )}
                                    />
                                </div>

                                {/* Month Selection */}
                                <div className="space-y-2">
                                    <label className={modalFieldLabelClass}>
                                        Select Month <span className="text-red-500">*</span>
                                    </label>
                                    <Select
                                        mode="multiple"
                                        value={formData.period === "month_wise" ? formData.month : formData.custom_month}
                                        onChange={(value) => handleInputChange(
                                            formData.period === "month_wise" ? "month" : "custom_month",
                                            value
                                        )}
                                        options={monthEnum.map(month => ({
                                            value: month.value,
                                            label: month.text
                                        }))}
                                        placeholder="Select month"
                                        disabled={loading}
                                        className="w-full"
                                        size="large"
                                        maxTagCount={2}
                                        maxTagTextLength={6}
                                    />
                                </div>
                            </div>

                            {/* Right Column */}
                            <div className="space-y-4">
                                {/* Start and End Days */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <label className={modalFieldLabelClass}>
                                            Start Day <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            value={formData.period === "month_wise" ? formData.start_day : formData.custom_start_day}
                                            onChange={(e) => handleInputChange(
                                                formData.period === "month_wise" ? "start_day" : "custom_start_day",
                                                e.target.value
                                            )}
                                            placeholder="1-31"
                                            maxLength={2}
                                            disabled={loading || formData.period === "month_wise"}
                                            onKeyDown={handleKeyDown}
                                            className={cn(
                                                "w-full rounded-md transition-colors duration-200",
                                                "border-gray-300 dark:border-gray-600",
                                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                            )}
                                        />
                                    </div>

                                    <div className="space-y-2">
                                        <label className={modalFieldLabelClass}>
                                            End Day <span className="text-red-500">*</span>
                                        </label>
                                        <Input
                                            value={formData.period === "month_wise" ? formData.end_day : formData.custom_end_day}
                                            onChange={(e) => handleInputChange(
                                                formData.period === "month_wise" ? "end_day" : "custom_end_day",
                                                e.target.value
                                            )}
                                            placeholder="1-31"
                                            maxLength={2}
                                            disabled={loading}
                                            onKeyDown={handleKeyDown}
                                            className={cn(
                                                "w-full rounded-md transition-colors duration-200",
                                                "border-gray-300 dark:border-gray-600",
                                                "focus:border-sidebar-primary focus:ring-2 focus:ring-sidebar-primary/20",
                                            )}
                                        />
                                    </div>
                                </div>

                                {/* Notes - spans full width of right column */}
                                <div className="space-y-2">
                                    <label className={modalFieldLabelClass}>
                                        Notes
                                    </label>
                                    <Textarea
                                        value={formData.notes}
                                        onChange={(e) => handleInputChange("notes", e.target.value)}
                                        placeholder="Enter any notes about this payroll period"
                                        disabled={loading}
                                        {...modalDescriptionFieldProps}
                                        className={modalTextareaClass}
                                    />
                                </div>

                                {/* Active Status */}
                                <div className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                                    <label className={modalFieldLabelClass}>
                                        Active Status
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <span className={cn(
                                            "text-xs font-medium",
                                            formData.is_active
                                                ? "text-green-600 dark:text-green-400"
                                                : "text-gray-500 dark:text-gray-400"
                                        )}>
                                            {formData.is_active ? "Active" : "Inactive"}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => handleInputChange("is_active", !formData.is_active)}
                                            className={cn(
                                                "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
                                                formData.is_active ? "bg-sidebar-primary" : "bg-gray-300 dark:bg-gray-600",
                                                loading || !editMode ? "cursor-not-allowed opacity-50" : "cursor-pointer"
                                            )}
                                            disabled={loading || !editMode}
                                        >
                                            <span
                                                className={cn(
                                                    "inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform duration-200",
                                                    formData.is_active ? "translate-x-4" : "translate-x-1"
                                                )}
                                            />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <p className="text-xs text-gray-500 dark:text-gray-400 pt-2">
                        <span className="text-red-500">*</span> indicates required field
                    </p>
                </form>
            </DialogModal>

            {/* Delete Confirmation Modal */}
            <ConfirmPopupModal
                open={isDeleteConfirmOpen}
                onClose={() => {
                    setIsDeleteConfirmOpen(false);
                    setSelectedRow(null);
                }}
                onConfirm={handleDraftDelete}
                title="Delete Draft Payroll Period"
                description="Are you sure you want to delete this draft payroll period? This action cannot be undone."
                type="warning"
                confirmLabel="Delete"
                destructive={true}
                loading={loading}
            />
        </ContainerCard>
    );
};

export default PayrollPeriod;